import * as client from 'client';
import {localDateSender} from 'helpers/UnitsFormatHandler';
import {type ResolutionEnum} from 'interfaces';
import axios, {type AxiosInstance, type CancelToken} from 'axios';
import {
  type AddDelousing,
  type AddSiteDelousing,
  type AddSalesPriceModel,
  type AddSalesPriceModelAssignment,
  type AddQualityGradingModel,
  type AddQualityGradingModelAssignment,
  type EditDelousing,
  type EditSalesPriceModelCell,
  type PlanningForecastApiForecastGetRequest,
  type PlanningRecommendHarvestApiRecommendHarvestsGetRequest,
  type SalesPriceModelRow,
  type UpdateReadNotification,
  type WriteNotification,
  type KPIsKeyPerformanceIndicatorsApiKpiGetRequest,
  type KPIsKeyPerformanceIndicatorsApiDataReportPlanIdGetRequest,
  type KPIsKeyPerformanceIndicatorsApiInputTimelineGetRequest,
  type Region,
  type SalesPriceModelAssignment,
  type SalesPriceModel,
  type SalesClassModelTypeModelTypeEnum,
  type QualityGradingModel,
  type QualityGradingModelAssignment,
  type CreateBioplanExternalAPIConnection,
  type PlanningActionApiActionsGetRequest,
  type CageTimeSeriesRequestFieldNamesEnum,
  type SyncMapping,
  type LiceSourceOrder,
  type ActionDelete,
  type ModellingGrowthApiGrowthModelsGrowthModelIdPutRequest,
} from 'client';
import {getBaseUrl} from '../utils/runtimeVars';

const BASE_URL = getBaseUrl('')!;
const TEST_URL = import.meta.env.TESTS_API_URL ?? '';
const API_URL = import.meta.env.NODE_ENV === 'test' ? TEST_URL : BASE_URL;

export class BackendCommunicator {
  private readonly configuration: client.Configuration;
  private readonly plan: client.PlanningPlanApi;
  private readonly site: client.PlanningSiteApi;
  private readonly siteFallowing: client.PlanningFallowingApi;
  private readonly cage: client.PlanningCageApi;
  private readonly input: client.PlanningInputApi;
  private readonly harvest: client.PlanningHarvestApi;
  private readonly forecast: client.PlanningForecastApi;
  private readonly forecastState: client.PlanningForecastStateApi;
  private readonly optimize: client.OptimizationOptimizeApi;
  private readonly concession: client.PlanningConcessionApi;
  private readonly fishType: client.PlanningFishTypeApi;
  private readonly KPI: client.KPIsKeyPerformanceIndicatorsApi;
  private readonly optimizationSettings: client.OptimizationSettingsApi;
  private readonly dataIntegration: client.DataIntegrationApi;
  private readonly tenant: client.OrganisationTenantApi;
  private readonly user: client.OrganisationUserApi;
  private readonly growthModels: client.ModellingGrowthApi;
  private readonly modellingSGR: client.ModellingSgrApi;
  private readonly siteGrowthModelOverrides: client.ModellingGrowthModelOverridesApi;
  private readonly actions: client.PlanningActionApi;
  private readonly globalSettings: client.ModellingGlobalSettingsApi;
  private readonly transfer: client.PlanningTransferApi;
  private readonly navigation: client.FrontendComponentsNavigationTreeApi;
  private readonly recommendHarvest: client.PlanningRecommendHarvestApi;
  private readonly productionHistory: client.ProductionHistoryApi;
  private readonly smoltSupply: client.PlanningSmoltSupplyApi;
  private readonly priceModels: client.SalesPriceModelsApi;
  private readonly priceModelAssignments: client.SalesPriceModelAssignmentApi;
  private readonly qualityGradingModels: client.QualityGradingModelsApi;
  private readonly qualityGradingModelAssignments: client.QualityGradingModelAssignmentApi;
  // private readonly qualityGradingModelTemplates: client.QualityGradingModelTemplatesApi;
  private readonly qualityGradingModelsOverviewTable: client.QualityGradingModelsOverviewTableApi;
  private readonly fcrModels: client.ModellingFeedConversionRatioModelsApi;
  private readonly version: client.VersionApi;
  private readonly harvestDemand: client.PlanningHarvestDemandApi;
  private readonly environmentLice: client.EnvironmentLiceApi;
  private readonly liveFishTransaction: client.PlanningLiveFishTransactionApi;
  private readonly notifications: client.NotificationsApi;
  private readonly delousing: client.PlanningDelousingApi;
  private readonly harvestPlant: client.PlanningHarvestPlantsApi;
  private readonly modellingTemperature: client.ModellingTemperatureApi;
  private readonly cageForecast: client.PlanningCageForecastApi;
  private readonly harvestReports: client.ReportingHarvestApi;
  private readonly region: client.PlanningRegionApi;
  private readonly planSettings: client.PlanningPlanSettingsApi;
  private readonly folder: client.PlanningFoldersApi;
  private readonly publish: client.PublishApi;
  private readonly mortalityModels: client.MortalityModelsApi;
  private readonly mortalityModelAssignments: client.MortalityModelAssignmentApi;
  private readonly fishGroups: client.PlanningFishGroupApi;
  private readonly sensorData: client.SensorDataApi;
  private readonly company: client.PlanningCompanyApi;
  private readonly fishGroupProperties: client.PlanningFishGroupPropertiesApi;
  private readonly siteConcession: client.PlanningSiteConcessionApi;

  //* ************* Plans **************//
  async getPlans() {
    return await this.plan.plansGet();
  }

  async getPlanSimple(planId: string) {
    return await this.plan.plansPlanIdGet({planId});
  }

  async createPlan(newPlan: client.WritePlan) {
    return await this.plan.plansPost({writePlan: newPlan});
  }

  async updatePlan(planId: string, editPlan: client.EditPlan) {
    return await this.plan.plansPlanIdPut({planId, editPlan});
  }

  async copyPlan(copyPlan: client.CopyPlan) {
    return await this.plan.plansCopyPost({copyPlan});
  }

  async deletePlan(planId: string) {
    return await this.plan.plansPlanIdDelete({planId});
  }

  async createPlanUsingProductionHistory(writePlanUsingProductionHistory: client.WritePlanUsingProductionHistory) {
    return await this.plan.plansProductionHistoryPost({writePlanUsingProductionHistory});
  }

  async getPlanSynchronizationDraft(planId: string, date: Date) {
    const _date = localDateSender(date);
    return await this.plan.plansPlanIdSyncDraftGet({planId, date: _date});
  }

  async synchronizePlanActions(
    planId: string,
    date: Date,
    actionsToKeep: client.SyncPlanAction[],
    mappings?: SyncMapping[],
  ) {
    const _date = localDateSender(date);
    return await this.plan.plansPlanIdSyncActionsPost({
      planId,
      syncPlanActionsRequest: {date: _date, actionsToKeep, mappings},
    });
  }

  async importPlan(importPlan: client.ExportImport) {
    return await this.plan.plansImportPost({exportImport: importPlan});
  }

  async exportPlan(planId: string) {
    return await this.plan.plansPlanIdExportGet({planId});
  }

  async getPlanHarvests(planId: string, _startDate?: Date, _endDate?: Date) {
    const startDate = _startDate && localDateSender(_startDate);
    const endDate = _endDate && localDateSender(_endDate);
    return await this.harvest.harvestsGet({planId, startDate, endDate});
  }

  async getPlanInputs(planId: string, _startDate?: Date, _endDate?: Date) {
    const startDate = _startDate && localDateSender(_startDate);
    const endDate = _endDate && localDateSender(_endDate);
    return await this.input.inputsGet({planId, startDate, endDate});
  }

  async getPlanOrSmoltSupplyInputs(planId: string, smoltSupplyIds: string[]) {
    return await this.input.inputsGet({planId, smoltSupplyIds});
  }

  async getUnAssignedInputsToSmoltSupply(planId: string) {
    return await this.input.inputsNotLinkedToSmoltSuppliesGet({planId});
  }

  async getPlanTreeNavigation(planId: string) {
    return await this.navigation.frontendComponentsNavigationTreePlanIdGet({planId});
  }

  async getPlanHarvestRecommendation(
    planId: string,
    sortBy: PlanningRecommendHarvestApiRecommendHarvestsGetRequest['sortBy'],
    date: Date,
    siteId?: string,
    biomass?: number,
    excludeWithProperties?: string[],
  ) {
    const _date = localDateSender(date);
    return await this.recommendHarvest.recommendHarvestsGet({
      planId,
      siteId,
      date: _date,
      biomass,
      sortBy,
      excludeWithProperties,
    });
  }

  async getPlanTimeseriesFile(planId: string, columns: string[], _startDate: Date, _endDate: Date) {
    const startDate = localDateSender(_startDate);
    const endDate = localDateSender(_endDate);
    return await this.plan.plansPlanIdTimeseriesGet({
      planId,
      columns: columns as client.PlansPlanIdTimeseriesGetColumnsEnum,
      startDate,
      endDate,
      fileFormat: 'csv',
    });
  }

  async getPlanHistory(planId: string) {
    return await this.plan.plansPlanIdHistoryGet({planId});
  }

  async getPlanStatus(planId: string) {
    return await this.plan.plansPlanIdPollingGet({planId});
  }

  //* ************* Folders **************//
  async getFolders() {
    return await this.folder.foldersGet();
  }

  async createFolder(name: string) {
    return await this.folder.foldersPost({addEditFolder: {name}});
  }

  async updateFolder(folderId: string, name: string) {
    return await this.folder.foldersFolderIdPut({folderId, addEditFolder: {name}});
  }

  async deleteFolder(folderId: string) {
    return await this.folder.foldersFolderIdDelete({folderId});
  }

  //* ************* Regions **************//
  async getRegions(planId: string) {
    return await this.region.regionsGet({planId});
  }

  async createRegion(region: Region) {
    return await this.region.regionsPost({region});
  }

  async updateRegion(regionId: string, region: Region) {
    return await this.region.regionsRegionIdPut({regionId, region});
  }

  async deleteRegion(regionId: string) {
    return await this.region.regionsRegionIdDelete({regionId});
  }

  //* ************* Publish **************//
  async publishPlan(type: 'harvest' | 'production', name: string, comment: string, planId: string) {
    return await this.publish.publishPut({name, planId, type, comment});
  }

  async publishPlanPreview(type: 'harvest' | 'production', name: string, planId: string) {
    return await this.publish.publishPreviewGet({name, planId, type});
  }

  //* ************* Sites **************//
  async createSite(writeSite: client.WriteSite) {
    return await this.site.sitesPost({writeSite});
  }

  /**
   * @deprecated
   */
  async getSite(siteId: string) {
    return await this.site.sitesSiteIdGet({siteId});
  }

  async getSiteSimple(siteId: string) {
    return await this.site.sitesSiteIdSimpleGet({siteId});
  }

  async getSites(planId: string) {
    return await this.site.sitesGet({planId});
  }

  async updateSite(siteId: string, writeSite: client.WriteSite) {
    return await this.site.sitesSiteIdPut({siteId, writeSite});
  }

  async deleteSite(siteId: string) {
    return await this.site.sitesSiteIdDelete({siteId});
  }

  async getSiteGrowthModelOverrides(planId: string) {
    return await this.siteGrowthModelOverrides.growthModelOverridesGet({planId});
  }

  async createSiteGrowthModelOverride(growthModelOverride: client.GrowthModelOverride) {
    return await this.siteGrowthModelOverrides.growthModelOverridesPost({growthModelOverride});
  }

  async deleteSiteGrowthModelOverride(growthModelOverrideId: string) {
    return await this.siteGrowthModelOverrides.growthModelOverridesGrowthModelOverrideIdDelete({growthModelOverrideId});
  }

  async getSiteHarvests(siteId: string, _startDate?: Date, _endDate?: Date) {
    const startDate = _startDate && localDateSender(_startDate);
    const endDate = _endDate && localDateSender(_endDate);
    return await this.harvest.harvestsGet({siteId, startDate, endDate});
  }

  async getSiteInputs(planId: string, siteId: string, _startDate?: Date, _endDate?: Date) {
    const startDate = _startDate && localDateSender(_startDate);
    const endDate = _endDate && localDateSender(_endDate);
    return await this.input.inputsGet({planId, siteId, startDate, endDate});
  }

  //* ************* Cages **************//
  async createCage(writeCage: client.WriteCage) {
    return await this.cage.cagesPost({writeCage});
  }

  async updateCage(cageId: string, writeCage: client.WriteCage) {
    return await this.cage.cagesCageIdPut({cageId, writeCage});
  }

  async deleteCage(cageId: string) {
    return await this.cage.cagesCageIdDelete({cageId});
  }

  async cagesAvailableFishGroupQualityGet(cageId: string, date: string) {
    return await this.cage.cagesCageIdAvailableFishGroupQualityGet({cageId, date});
  }

  /**
   * @deprecated use getCageSimple
   */
  async getCage(cageId: string) {
    return await this.cage.cagesCageIdGet({cageId});
  }

  async getCageSimple(cageId: string) {
    return await this.cage.cagesCageIdSimpleGet({cageId});
  }

  async getCages(planId?: string, siteId?: string) {
    return await this.cage.cagesGet({planId, siteId});
  }

  async getEmptyCages(siteId: string, date: Date) {
    return await this.cage.cagesEmptyGet({siteId, date: localDateSender(date)});
  }

  async getNonEmptyCages(siteId: string, date: Date) {
    return await this.cage.cagesNonEmptyGet({siteId, date: localDateSender(date)});
  }

  async getCageTimeSeries(
    cageId: string,
    fieldNames: CageTimeSeriesRequestFieldNamesEnum[],
    startDate?: Date,
    endDate?: Date,
  ) {
    const _startDate = startDate && localDateSender(startDate);
    const _endDate = endDate && localDateSender(endDate);
    return await this.cage.cagesCageIdTimeseriesPost({
      cageId,
      cageTimeSeriesRequest: {fieldNames: fieldNames.flat(), startDate: _startDate, endDate: _endDate},
    });
  }

  //* ************* Input **************//
  async getInput(planId: string, inputId: string) {
    return await this.input.inputsInputIdGet({planId, inputId});
  }

  async addCageInput(inputPost: client.InputPost) {
    return await this.input.inputsPost({inputPost});
  }

  async addCageBatchInput(inputPost: client.InputPost[]) {
    return await this.input.inputsBatchPost({inputPost});
  }

  async updateCageInput(inputs: client.InputPut[]) {
    return await this.input.inputsPut({inputPut: inputs});
  }

  async deleteCageInput(id: client.PlanningInputApiInputsInputIdDeleteRequest) {
    return await this.input.inputsInputIdDelete(id);
  }

  async getCageInputs(planId: string, cageId: string) {
    return await this.input.inputsGet({planId, cageId});
  }

  async partialUpdateInput(inputId: string, inputPatch: client.InputPatch) {
    return await this.input.inputsInputIdPatch({inputId, inputPatch});
  }

  //* ************* Cage Harvest **************//
  async addHarvest(createHarvest: client.CreateHarvest) {
    return await this.harvest.harvestsPost({createHarvest});
  }

  async updateHarvest(harvestId: string, createHarvest: client.CreateHarvest) {
    return await this.harvest.harvestsHarvestIdPut({harvestId, createHarvest});
  }

  async partialUpdateHarvest(harvestId: string, patchHarvest: client.PatchHarvest) {
    return await this.harvest.harvestsHarvestIdPatch({harvestId, patchHarvest});
  }

  async deleteCageHarvest(harvestId: string) {
    return await this.harvest.harvestsHarvestIdDelete({harvestId});
  }

  async getCageHarvests(cageId: string, _startDate?: Date, _endDate?: Date) {
    const startDate = _startDate && localDateSender(_startDate);
    const endDate = _endDate && localDateSender(_endDate);
    return await this.harvest.harvestsGet({cageId, startDate, endDate});
  }

  async getHarvestsWithDistribution(planIds: string[], hogWeight: boolean) {
    return await this.harvest.harvestsOverviewGet({planIds, hogWeight});
  }

  async getHarvest(harvestId: string) {
    return await this.harvest.harvestsHarvestIdGet({harvestId});
  }

  async batchEditHarvests(cageId: string, batchEditHarvests: client.BatchEditHarvests) {
    return await this.harvest.harvestsBulkCageIdPatch({cageId, batchEditHarvests});
  }

  async addHarvestSequence(createHarvestSequence: client.CreateHarvestSequence) {
    return await this.harvest.harvestSequencePost({createHarvestSequence});
  }

  async getHarvestQualityDistribution(harvestId: string, hogWeight: boolean) {
    return await this.harvest.harvestsHarvestIdQualityDistributionGet({harvestId, hogWeight});
  }

  //* ************* Site Fallowing **************//
  async getSiteFallowings(siteId: string) {
    return await this.siteFallowing.fallowingsGet({siteId});
  }

  async getPlanFallowings(planId: string) {
    return await this.siteFallowing.fallowingsGet({planId});
  }

  async getFallowing(fallowingId: string) {
    return await this.siteFallowing.fallowingsFallowingIdGet({fallowingId});
  }

  async addSiteFallowing(writeFallowing: client.WriteFallowing) {
    return await this.siteFallowing.fallowingsPost({writeFallowing});
  }

  async updateSiteFallowing(fallowingId: string, writeFallowing: client.WriteFallowing) {
    return await this.siteFallowing.fallowingsFallowingIdPut({fallowingId, writeFallowing});
  }

  async deleteSiteFallowing(fallowingId: string) {
    return await this.siteFallowing.fallowingsFallowingIdDelete({fallowingId});
  }

  //* ************* Forecast **************//
  async getForecast(
    siteId?: string[] | undefined,
    cageId?: string[] | undefined,
    startDate?: Date,
    endDate?: Date,
    groupBy?: PlanningForecastApiForecastGetRequest['groupBy'],
    columns?: PlanningForecastApiForecastGetRequest['columns'],
  ) {
    const _startDate = startDate && localDateSender(startDate);
    const _endDate = endDate && localDateSender(endDate);
    return await this.forecast.forecastGet({
      siteId,
      cageId,
      startDate: _startDate,
      endDate: _endDate,
      groupBy,
      columns,
    });
  }

  async getPlanForecast(
    planId: string[],
    startDate?: Date,
    endDate?: Date,
    groupBy?: PlanningForecastApiForecastGetRequest['groupBy'],
    columns?: PlanningForecastApiForecastGetRequest['columns'],
  ) {
    const _startDate = startDate && localDateSender(startDate);
    const _endDate = endDate && localDateSender(endDate);
    return await this.forecast.forecastGet({
      planId,
      startDate: _startDate,
      endDate: _endDate,
      groupBy,
      columns,
    });
  }

  async getForecastState(date: Date, cageId: string) {
    const useDateAsStarvationTrigger = true;
    const _date = localDateSender(date);
    return await this.forecastState.forecastStateGet({useDateAsStarvationTrigger, cageId, date: _date});
  }

  async getCageGrowthForecast(planId: string) {
    return await this.forecast.forecastGrowthPerCagePlanIdGet({planId});
  }

  async getCageMortalityForecast(planId: string) {
    return await this.forecast.forecastMortalityPerCagePlanIdGet({planId});
  }

  async getSiteStatus(siteId: string) {
    return await this.forecast.forecastSiteStatusSiteIdGet({siteId});
  }

  async getCageQualityDistributionForecast(cageId: string, startDate?: Date, endDate?: Date) {
    const _startDate = startDate ? localDateSender(startDate) : undefined;
    const _endDate = endDate ? localDateSender(endDate) : undefined;
    return await this.forecast.forecastCageIdQualityForecastGet({cageId, startDate: _startDate, endDate: _endDate});
  }

  //* ************* Concessions **************//
  async getConcessions(planId: string) {
    return await this.concession.concessionsGet({planId});
  }

  async getConcession(concessionId: string) {
    return await this.concession.concessionsConcessionIdGet({concessionId});
  }

  async addConcessions(concession: client.Concession) {
    return await this.concession.concessionsPost({concession});
  }

  async updateConcessions(concessionId: string, concession: client.Concession) {
    return await this.concession.concessionsConcessionIdPut({concessionId, concession});
  }

  async deleteConcessions(concessionId: string) {
    return await this.concession.concessionsConcessionIdDelete({concessionId});
  }

  async getCagesWithUnallocatedBiomass(planId: string) {
    return await this.concession.concessionsUnallocatedBiomassPlanIdGet({planId});
  }

  //* ************* Fish Type **************//
  async getFishTypes(planId: string) {
    return await this.fishType.fishTypesGet({planId});
  }

  async addFishType(fishTypePost: client.FishTypePost) {
    return await this.fishType.fishTypesPost({fishTypePost});
  }

  async deleteFishType(planId: string, fishTypeId: string) {
    return await this.fishType.fishTypesFishTypeIdDelete({planId, fishTypeId});
  }

  async updateFishType(fishTypeId: string, fishTypePut: client.FishTypePut) {
    return await this.fishType.fishTypesFishTypeIdPut({fishTypeId, fishTypePut});
  }

  //* ************* Optimize **************//
  async runOptimization(writeOptimizationRun: client.WriteOptimizationRun) {
    return await this.optimize.optimizationRunsPost({writeOptimizationRun});
  }

  async getOptimizationRunState(runId: string) {
    return await this.optimize.optimizationRunsRunIdGet({runId});
  }

  async getPlanOptimizationState(planId: string) {
    return await this.optimize.optimizationRunsGet({planId, limit: 1});
  }

  async getAllOptimizationsRuns() {
    return await this.optimize.optimizationRunsGet();
  }

  async getOptimizationRun(runId: string) {
    return await this.optimize.optimizationRunsRunIdGet({runId});
  }

  async getOptimizationRunDetail(runId: string) {
    return await this.optimize.optimizationRunsRunIdStatusGet({runId});
  }

  async cancelOptimizationsRun(runId: string) {
    return await this.optimize.optimizationRunsRunIdCancelPut({runId});
  }

  //* ************* KPIs **************//
  async getMABUtilizationPerPlan(plansId: string[], resolution: ResolutionEnum, startDate: Date, endDate: Date) {
    const _plansId = plansId.filter((id) => Boolean(id));
    const _startDate = localDateSender(startDate);
    const _endDate = localDateSender(endDate);
    return await this.KPI.mABUtilizationGet({
      planId: _plansId,
      endDate: _endDate,
      startDate: _startDate,
      groupBy: resolution,
    });
  }

  async getMABUtilizationPerConcession(
    concessions: string[],
    resolution: ResolutionEnum,
    startDate?: Date,
    endDate?: Date,
  ) {
    const _startDate = startDate && localDateSender(startDate);
    const _endDate = endDate && localDateSender(endDate);
    return await this.KPI.mABUtilizationPerConcessionGet({
      concessions,
      endDate: _endDate,
      startDate: _startDate,
      groupBy: resolution,
    });
  }

  async getInputVolume(
    plansId: string[],
    startDate: Date,
    endDate: Date,
    resolution: ResolutionEnum,
    siteNames?: string[],
    species?: KPIsKeyPerformanceIndicatorsApiInputTimelineGetRequest['species'],
    certificates?: KPIsKeyPerformanceIndicatorsApiInputTimelineGetRequest['certificates'],
  ) {
    const _plansId = plansId.filter((id) => Boolean(id));
    const _startDate = localDateSender(startDate);
    const _endDate = localDateSender(endDate);

    return await this.KPI.inputTimelineGet({
      planId: _plansId,
      startDate: _startDate,
      endDate: _endDate,
      groupBy: resolution,
      siteNames,
      certificates,
      species,
    });
  }

  async getHarvestVolume(
    plansId: string[],
    startDate: Date,
    endDate: Date,
    resolution: ResolutionEnum,
    siteNames?: string[],
    species?: KPIsKeyPerformanceIndicatorsApiInputTimelineGetRequest['species'],
    certificates?: KPIsKeyPerformanceIndicatorsApiInputTimelineGetRequest['certificates'],
  ) {
    const _plansId = plansId.filter((id) => Boolean(id));
    const _startDate = localDateSender(startDate);
    const _endDate = localDateSender(endDate);
    return await this.KPI.harvestTimelineGet({
      planId: _plansId,
      startDate: _startDate,
      endDate: _endDate,
      groupBy: resolution,
      siteNames,
      certificates,
      species,
    });
  }

  async getPlanKpi(
    plansId: string[],
    startDate: Date,
    endDate: Date,
    columns?: KPIsKeyPerformanceIndicatorsApiKpiGetRequest['columns'],
    groupBy?: KPIsKeyPerformanceIndicatorsApiKpiGetRequest['groupBy'],
    siteNames?: string[],
  ) {
    const _plansId = plansId.filter((id) => Boolean(id));
    const _startDate = localDateSender(startDate);
    const _endDate = localDateSender(endDate);
    return await this.KPI.kpiGet({
      planId: _plansId,
      startDate: _startDate,
      endDate: _endDate,
      columns,
      groupBy,
      siteNames,
    });
  }

  async getEstimatedNetValue(requestData: {planId: string; endDate?: Date}, cancelToken: CancelToken) {
    const {planId, endDate} = requestData;
    const options = {cancelToken};
    const _endDate = endDate && localDateSender(endDate);
    return await this.KPI.estimatedNetValueGet({planId, endDate: _endDate}, options);
  }

  async getCageAllocationPerConcession(planId: string) {
    return await this.KPI.cageAllocationPerConcessionGet({planId});
  }

  async getDatareport(
    planId: string,
    groupBy: KPIsKeyPerformanceIndicatorsApiDataReportPlanIdGetRequest['groupBy'] = 'month',
  ) {
    return await this.KPI.dataReportPlanIdGet({planId, groupBy});
  }

  //* ************* Optimization Settings **************//
  async getOptimizationSettings(planId: string) {
    return await this.optimizationSettings.optimizationSettingsPlanIdGet({planId});
  }

  async updateOptimizationSettings(planId: string, optimizationSettings: client.OptimizationSettings) {
    return await this.optimizationSettings.optimizationSettingsPlanIdPut({planId, optimizationSettings});
  }

  //* ************* Data Integration **************//

  async getConnections() {
    return await this.dataIntegration.dataIntegrationConnectionsGet();
  }

  async createConnection(createDataIntegrationConnection: client.CreateDataIntegrationConnection) {
    return await this.dataIntegration.dataIntegrationConnectionsPost({createDataIntegrationConnection});
  }

  async createExternalAPIConnection(createBioplanExternalAPIConnection: CreateBioplanExternalAPIConnection) {
    return await this.dataIntegration.dataIntegrationConnectionsBioplanPost({createBioplanExternalAPIConnection});
  }

  async deleteConnection(connectionId: string) {
    return await this.dataIntegration.dataIntegrationConnectionsConnectionIdDelete({connectionId});
  }

  async checkConnectionState(connectionId: string) {
    return await this.dataIntegration.dataIntegrationConnectionsConnectionIdCheckGet({connectionId});
  }

  async forceRefreshDataIntegration(connectionId: string, toDate: Date, fromDate: Date) {
    const _toDate = localDateSender(toDate);
    const _fromDate = localDateSender(fromDate);
    return await this.dataIntegration.dataIntegrationConnectionsConnectionIdSynchronizePut({
      force: true,
      connectionId,
      toDate: _toDate,
      fromDate: _fromDate,
    });
  }

  //* ************* Company Management**************//

  async getTenants() {
    return await this.tenant.tenantsGet();
  }

  async getTenant(tenantId: string) {
    return await this.tenant.tenantsTenantIdGet({tenantId});
  }

  //* ************* User Management **************//

  async getUsers() {
    return await this.user.usersGet();
  }

  async updateUser(userId: string, writeUser: client.WriteUser) {
    return await this.user.usersUserIdPut({userId, writeUser});
  }

  async getMe() {
    return await this.user.usersMeGet();
  }

  //* ************* Growth Models **************//

  async getPlanGrowthModels(planId: string) {
    return await this.growthModels.growthModelsGet({planId});
  }

  async deleteGrowthModel(planId: string, growthModelId: string) {
    return await this.growthModels.growthModelsGrowthModelIdDelete({planId, growthModelId});
  }

  async getGrowthModel(planId: string, growthModelId: string) {
    return await this.growthModels.growthModelsGrowthModelIdGet({planId, growthModelId});
  }

  async updateGrowthModel(growthModel: ModellingGrowthApiGrowthModelsGrowthModelIdPutRequest) {
    return await this.growthModels.growthModelsGrowthModelIdPut(growthModel);
  }

  async getGrowthModelsTypes() {
    return await this.growthModels.growthModelsTypesGet();
  }

  /** Modelling SGR */
  async getSGRTemplate() {
    return await this.modellingSGR.sGRTemplateGet();
  }

  //* ************* Actions **************//
  async deleteBatchActions(actions: ActionDelete[], planId: string) {
    return await this.actions.actionsBatchDeletePost({actionBatchDelete: {actions, planId}});
  }

  async lockBatchActions(actionIds: string[]) {
    return await this.actions.actionsLockPut({batchModifyActions: {actionIds}});
  }

  async unlockBatchActions(actionIds: string[]) {
    return await this.actions.actionsUnlockPut({batchModifyActions: {actionIds}});
  }

  async getActions(
    planId: string[],
    _startDate?: Date,
    _endDate?: Date,
    actionType?: PlanningActionApiActionsGetRequest['actionType'],
    certificates?: PlanningActionApiActionsGetRequest['certificates'],
  ) {
    const startDate = _startDate && localDateSender(_startDate);
    const endDate = _endDate && localDateSender(_endDate);
    return await this.actions.actionsGet({planId, startDate, endDate, actionType, certificates});
  }

  async getSiteActions(planId: string, siteId: string, _startDate: Date, _endDate: Date) {
    const startDate = _startDate && localDateSender(_startDate);
    const endDate = _endDate && localDateSender(_endDate);
    return await this.actions.actionsGet({planId: [planId], siteId: [siteId], startDate, endDate});
  }

  async getDownloadActions(planId: string) {
    return await this.actions.actionsExportGet({planId: [planId]});
  }

  //* ************* Global Settings **************//
  async getGlobalSettings() {
    return await this.globalSettings.globalSettingsGet();
  }

  async updateGlobalSettings(globalSettings: client.GlobalSettings) {
    return await this.globalSettings.globalSettingsPut({globalSettings});
  }

  //* ************* Transfers **************//
  async addTransfer(writeTransfer: client.WriteTransfer) {
    return await this.transfer.transfersPost({writeTransfer});
  }

  async getPlanTransfers(planId: string) {
    return await this.transfer.transfersGet({planId});
  }

  async getSiteTransfers(siteId: string) {
    return await this.transfer.transfersGet({siteId});
  }

  async deleteTransfer(transferId: string) {
    return await this.transfer.transfersTransferIdDelete({transferId});
  }

  async getTransfer(transferId: string) {
    return await this.transfer.transfersTransferIdGet({transferId});
  }

  async updateTransfer(transferId: string, editTransfer: client.EditTransfer) {
    return await this.transfer.transfersTransferIdPut({transferId, editTransfer});
  }

  //* ************* Production History **************//

  async uploadProductionHistoryGeneric(file: File) {
    return await this.productionHistory.productionHistoryGenericPost({file});
  }

  async uploadProductionHistoryFishtalk(file: File) {
    return await this.productionHistory.productionHistoryFishtalkPost({file});
  }

  async uploadProductionHistoryMercatus(file: File) {
    return await this.productionHistory.productionHistoryMercatusPost({file});
  }

  async getProductionHistoryStats() {
    return await this.productionHistory.productionHistoryStatsGet();
  }

  async deleteProductionHistory() {
    return await this.productionHistory.productionHistoryDelete();
  }

  async downloadProductionHistory() {
    return await this.productionHistory.productionHistoryExportGet();
  }

  //* ************* Smolt Supply **************//

  async getSmoltSupply(planId: string, smoltSupplyId: string) {
    return await this.smoltSupply.smoltSuppliesSmoltSupplyIdGet({planId, smoltSupplyId});
  }

  async getSmoltSupplies(planId: string) {
    return await this.smoltSupply.smoltSuppliesGet({planId});
  }

  async addSmoltSupply(smoltSupplyPost: client.SmoltSupplyPost) {
    return await this.smoltSupply.smoltSuppliesPost({smoltSupplyPost});
  }

  async batchAddSmoltSupply(smoltSupplyBatchAddPost: client.SmoltSupplyBatchAddPost) {
    return await this.smoltSupply.smoltSuppliesBatchAddPost({smoltSupplyBatchAddPost});
  }

  async updateSmoltSupply(smoltSupplyId: string, smoltSupplyPut: client.SmoltSupplyPut) {
    return await this.smoltSupply.smoltSuppliesSmoltSupplyIdPut({smoltSupplyId, smoltSupplyPut});
  }

  async deleteSmoltSupply(planId: string, smoltSupplyId: string) {
    return await this.smoltSupply.smoltSuppliesSmoltSupplyIdDelete({planId, smoltSupplyId});
  }

  async batchDeleteSmoltSupply(smoltSupplyBatchDelete: client.SmoltSupplyBatchDelete) {
    return await this.smoltSupply.smoltSuppliesBatchDeletePost({smoltSupplyBatchDelete});
  }

  //* ************* Price Models **************//

  async getPriceModels(planId: string) {
    return await this.priceModels.salesPriceModelsGet({planId});
  }

  async getPriceModel(modelId: string) {
    return await this.priceModels.salesPriceModelsModelIdGet({modelId});
  }

  async addPriceModel(addSalesPriceModel: AddSalesPriceModel) {
    return await this.priceModels.salesPriceModelsPost({addSalesPriceModel});
  }

  async updatePriceModel(modelId: string, salesPriceModel: SalesPriceModel) {
    return await this.priceModels.salesPriceModelsModelIdPut({modelId, salesPriceModel});
  }

  async copyPriceModel(id: string, name: string) {
    return await this.priceModels.salesPriceModelsCopyPost({copySalesPriceModel: {id, name}});
  }

  async deletePriceModel(modelId: string) {
    return await this.priceModels.salesPriceModelsModelIdDelete({modelId});
  }

  async getPriceModelRows(modelId: string) {
    return await this.priceModels.salesPriceModelsModelIdRowsGet({modelId});
  }

  async addPriceModelRow(modelId: string, salesPriceModelRow: SalesPriceModelRow) {
    return await this.priceModels.salesPriceModelsModelIdRowsPost({modelId, salesPriceModelRow});
  }

  async updatePriceModelRow(modelId: string, fromDate: string, salesPriceModelRow: SalesPriceModelRow) {
    return await this.priceModels.salesPriceModelsModelIdRowsFromDatePut({modelId, fromDate, salesPriceModelRow});
  }

  async updatePriceModelSinglePrice(modelId: string, fromDate: string, value: EditSalesPriceModelCell) {
    return await this.priceModels.salesPriceModelsModelIdRowsFromDatePatch({
      modelId,
      fromDate,
      editSalesPriceModelCell: value,
    });
  }

  async deletePriceModelRow(modelId: string, fromDate: string) {
    return await this.priceModels.salesPriceModelsModelIdRowsFromDateDelete({modelId, fromDate});
  }

  async getPriceModelTypes() {
    return await this.priceModels.salesPriceModelsTypesGet();
  }

  async getPriceModelTypeForModel(modelType: string) {
    return await this.priceModels.salesPriceModelsTypesModelTypeGet({modelType});
  }

  async uploadPriceModel(planId: string, name: string, file: File, type: SalesClassModelTypeModelTypeEnum) {
    return await this.priceModels.salesPriceModelsImportPost({planId, name, file, type});
  }

  async downloadPriceModelTemplate() {
    return await this.priceModels.salesPriceModelsImportTemplateGet();
  }

  async getPriceModelAssigments(planId: string) {
    return await this.priceModelAssignments.salesPriceModelAssignmentsGet({planId});
  }

  async addPriceModelAssignment(addSalesPriceModelAssignment: AddSalesPriceModelAssignment) {
    return await this.priceModelAssignments.salesPriceModelAssignmentsPost({addSalesPriceModelAssignment});
  }

  async editPriceModelAssignment(salesPriceModelAssignment: SalesPriceModelAssignment) {
    return await this.priceModelAssignments.salesPriceModelAssignmentsAssignmentIdPut({
      assignmentId: salesPriceModelAssignment.id,
      salesPriceModelAssignment,
    });
  }

  async deletePriceModelAssignment(assignmentId: string) {
    return await this.priceModelAssignments.salesPriceModelAssignmentsAssignmentIdDelete({assignmentId});
  }

  //* ************* Quality Grading **************//

  async getQualityGradings(planId: string) {
    return await this.qualityGradingModels.qualityGradingModelsGet({planId});
  }

  async getQualityGrading(qualityGradingModelId: string) {
    return await this.qualityGradingModels.qualityGradingModelsModelIdGet({modelId: qualityGradingModelId});
  }

  async addQualityGrading(qualityGradingModel: AddQualityGradingModel) {
    return await this.qualityGradingModels.qualityGradingModelsPost({addQualityGradingModel: qualityGradingModel});
  }

  async updateQualityGrading(qualityGradingModelId: string, qualityGradingModel: QualityGradingModel) {
    return await this.qualityGradingModels.qualityGradingModelsModelIdPut({
      modelId: qualityGradingModelId,
      qualityGradingModel,
    });
  }

  async deleteQualityGrading(qualityGradingModelId: string) {
    return await this.qualityGradingModels.qualityGradingModelsModelIdDelete({modelId: qualityGradingModelId});
  }

  async copyGradingModel(id: string, name: string) {
    return await this.qualityGradingModels.qualityGradingModelsCopyPost({copyQualityGradingModel: {id, name}});
  }

  async getQualityGradingModelAssigments(planId: string) {
    return await this.qualityGradingModelAssignments.qualityGradingModelAssignmentsGet({planId});
  }

  async addQualityGradingModelAssignment(addQualityGradingModelAssignment: AddQualityGradingModelAssignment) {
    return await this.qualityGradingModelAssignments.qualityGradingModelAssignmentsPost({
      addQualityGradingModelAssignment,
    });
  }

  async editQualityGradingModelAssignment(qualityGradingModelAssignment: QualityGradingModelAssignment) {
    return await this.qualityGradingModelAssignments.qualityGradingModelAssignmentsAssignmentIdPut({
      assignmentId: qualityGradingModelAssignment.id,
      qualityGradingModelAssignment,
    });
  }

  async deleteQualityGradingModelAssignment(assignmentId: string) {
    return await this.qualityGradingModelAssignments.qualityGradingModelAssignmentsAssignmentIdDelete({assignmentId});
  }

  async getSiteQualityGradingModelsOverviewTable(date: string, siteId: string) {
    return await this.qualityGradingModelsOverviewTable.qualityGradingModelsOverviewTableGet({
      date,
      siteId,
    });
  }

  async getCageQualityGradingModelsOverviewTable(date: string, cageId: string) {
    return await this.qualityGradingModelsOverviewTable.qualityGradingModelsOverviewTableGet({
      date,
      cageId,
    });
  }

  //* ************* FCR Models **************//

  async getFCRModels(planId: string) {
    return await this.fcrModels.feedConversionRatioModelsGet({planId});
  }

  async getFCRModel(planId: string, modelId: string) {
    return await this.fcrModels.feedConversionRatioModelsModelIdGet({planId, modelId});
  }

  async addFCRModel(fCRModelPost: client.FCRModelPost) {
    return await this.fcrModels.feedConversionRatioModelsPost({fCRModelPost});
  }

  async updateFCRModel(modelId: string, fCRModelPut: client.FCRModelPut) {
    return await this.fcrModels.feedConversionRatioModelsModelIdPut({modelId, fCRModelPut});
  }

  async deleteFCRModel(planId: string, modelId: string) {
    return await this.fcrModels.feedConversionRatioModelsModelIdDelete({planId, modelId});
  }

  async getFCRModelRows(planId: string, modelId: string) {
    return await this.fcrModels.feedConversionRatioModelsModelIdRowsGet({planId, modelId});
  }

  async updateFCRModelRow(modelId: string, fCRModelRowPut: client.FCRModelRowPut) {
    return await this.fcrModels.feedConversionRatioModelsModelIdRowsPut({modelId, fCRModelRowPut});
  }

  async addFCRModelRow(modelId: string, fCRModelRowPost: client.FCRModelRowPost) {
    return await this.fcrModels.feedConversionRatioModelsModelIdRowsPost({modelId, fCRModelRowPost});
  }

  async deleteFCRModelRow({modelId, id, planId}: {modelId: string; id: string; planId: string}) {
    return await this.fcrModels.feedConversionRatioModelsModelIdRowsDelete({
      modelId,
      id,
      planId,
    });
  }

  //* ************* Version **************//

  async getAPIVersion() {
    return await this.version.versionGet();
  }

  //* ************* Harvest Demand API **************//

  async getHarvestDemands(planId: string) {
    return await this.harvestDemand.harvestDemandsGet({planId});
  }

  async getHarvestDemand(planId: string, harvestDemandId: string) {
    return await this.harvestDemand.harvestDemandsHarvestDemandIdGet({planId, harvestDemandId});
  }

  async addHarvestDemand(harvestDemandPost: client.HarvestDemandPost) {
    return await this.harvestDemand.harvestDemandsPost({harvestDemandPost});
  }

  async updateHarvestDemand(harvestDemandId: string, harvestDemandPut: client.HarvestDemandPut) {
    return await this.harvestDemand.harvestDemandsHarvestDemandIdPut({harvestDemandId, harvestDemandPut});
  }

  async deleteHarvestDemand(planId: string, harvestDemandId: string) {
    return await this.harvestDemand.harvestDemandsHarvestDemandIdDelete({planId, harvestDemandId});
  }

  //* ************* Lice API **************//
  async getLice(date: string, siteId: string, cageId?: string) {
    return await this.environmentLice.liceGet({date, siteId, cageId});
  }

  async getLiceSources() {
    return await this.environmentLice.liceSourcesGet();
  }

  async getActiveLiceSources() {
    return await this.environmentLice.liceSourcesActiveGet();
  }

  async resetLiceSources() {
    return await this.environmentLice.liceSourcesResetPut();
  }

  async reorderLiceSources(liceSourceOrder: LiceSourceOrder) {
    return await this.environmentLice.liceSourcesReorderPut({liceSourceOrder});
  }

  // ************** Live Fish Transaction API ******************//
  async getLiveFishTransaction(planId: string, transactionId: string) {
    return await this.liveFishTransaction.liveFishTransactionsTransactionIdGet({planId, transactionId});
  }

  async getLiveFishTransactions(planId: string) {
    return await this.liveFishTransaction.liveFishTransactionsGet({planId});
  }

  async getCageLiveFishTransactions(planId: string, cageId: string) {
    return await this.liveFishTransaction.liveFishTransactionsGet({planId, cageId});
  }

  async addLiveFishTransaction(liveFishTransactionPost: client.LiveFishTransactionPost) {
    return await this.liveFishTransaction.liveFishTransactionsPost({liveFishTransactionPost});
  }

  async updateLiveFishTransaction(transactionId: string, liveFishTransactionPut: client.LiveFishTransactionPut) {
    return await this.liveFishTransaction.liveFishTransactionsTransactionIdPut({
      transactionId,
      liveFishTransactionPut,
    });
  }

  async patchLiveFishTransaction(transactionId: string, liveFishTransactionPatch: client.LiveFishTransactionPatch) {
    return await this.liveFishTransaction.liveFishTransactionsTransactionIdPatch({
      transactionId,
      liveFishTransactionPatch,
    });
  }

  async deleteLiveFishTransaction(planId: string, transactionId: string) {
    return await this.liveFishTransaction.liveFishTransactionsTransactionIdDelete({planId, transactionId});
  }

  //* ************* Notifications API **************//
  async getNotifications(planId: string) {
    return await this.notifications.notificationsGet({planId});
  }

  async batchUpdateNotifications(writeNotification: WriteNotification[]) {
    return await this.notifications.notificationsPatch({writeNotification});
  }

  async batchDeleteNotifications(planId: string) {
    return await this.notifications.notificationsDelete({planId});
  }

  async getNotification(notificationId: string) {
    return await this.notifications.notificationsNotificationIdGet({notificationId});
  }

  async updateNotification(notificationId: string, updateReadNotification: UpdateReadNotification) {
    return await this.notifications.notificationsNotificationIdPatch({notificationId, updateReadNotification});
  }

  async deleteNotification(notificationId: string) {
    return await this.notifications.notificationsNotificationIdDelete({notificationId});
  }

  //* ************* Delousing API **************//
  async addDelousing(addDelousing: AddDelousing) {
    return await this.delousing.delousingsPost({addDelousing});
  }

  async addSiteDelousing(addSiteDelousing: AddSiteDelousing) {
    return await this.delousing.delousingsSitePost({addSiteDelousing});
  }

  async updateDelousing(delousingId: string, editDelousing: EditDelousing) {
    return await this.delousing.delousingsDelousingIdPut({delousingId, editDelousing});
  }

  async deleteDelousing(delousingId: string) {
    return await this.delousing.delousingsDelousingIdDelete({delousingId});
  }

  async getSiteDelousing(siteId: string, _startDate?: Date, _endDate?: Date) {
    const startDate = _startDate && localDateSender(_startDate);
    const endDate = _endDate && localDateSender(_endDate);
    return await this.delousing.delousingsGet({siteId, startDate, endDate});
  }

  async getCageDelousings(cageId: string, _startDate?: Date, _endDate?: Date) {
    const startDate = _startDate && localDateSender(_startDate);
    const endDate = _endDate && localDateSender(_endDate);
    return await this.delousing.delousingsGet({cageId, startDate, endDate});
  }

  async getDelousing(delousingId: string) {
    return await this.delousing.delousingsDelousingIdGet({delousingId});
  }

  //* ************* Harvest Plant API **************//
  async getHarvestPlants(planIds: string[]) {
    return await this.harvestPlant.harvestPlantsGet({planIds});
  }

  async getHarvestPlant(harvestPlantId: string) {
    return await this.harvestPlant.harvestPlantsHarvestPlantIdGet({harvestPlantId});
  }

  async addHarvestPlant(createHarvestPlant: client.CreateHarvestPlant) {
    return await this.harvestPlant.harvestPlantsPost({createHarvestPlant});
  }

  async updateHarvestPlant(harvestPlantId: string, editHarvestPlant: client.EditHarvestPlant) {
    return await this.harvestPlant.harvestPlantsHarvestPlantIdPut({harvestPlantId, editHarvestPlant});
  }

  async deleteHarvestPlant(harvestPlantId: string) {
    return await this.harvestPlant.harvestPlantsHarvestPlantIdDelete({harvestPlantId}); //
  }

  async getHarvestPlantAvailable(siteId: string) {
    return await this.harvestPlant.harvestPlantsAvailableGet({siteId});
  }

  //* ************* Harvest Plant / Shifst API **************//
  async addHarvestPlantShift(harvestPlantId: string, createHarvestPlantShift: client.CreateHarvestPlantShift) {
    return await this.harvestPlant.harvestPlantsHarvestPlantIdShiftsPost({
      harvestPlantId,
      createHarvestPlantShift,
    });
  }

  async updateHarvestPlantShift(
    harvestPlantId: string,
    harvestPlantShiftId: string,
    editHarvestPlantShift: client.EditHarvestPlantShift,
  ) {
    return await this.harvestPlant.harvestPlantsHarvestPlantIdShiftsHarvestPlantShiftIdPut({
      harvestPlantId,
      harvestPlantShiftId,
      editHarvestPlantShift,
    });
  }

  async getHarvestPlantShift(harvestPlantId: string, harvestPlantShiftId: string) {
    return await this.harvestPlant.harvestPlantsHarvestPlantIdShiftsHarvestPlantShiftIdGet({
      harvestPlantId,
      harvestPlantShiftId,
    });
  }

  async deleteHarvestPlantShift(harvestPlantId: string, harvestPlantShiftId: string) {
    return await this.harvestPlant.harvestPlantsHarvestPlantIdShiftsHarvestPlantShiftIdDelete({
      harvestPlantId,
      harvestPlantShiftId,
    });
  }

  async getHarvestPlantsCapacity(planId: string, _startDate: Date, _endDate: Date) {
    const startDate = localDateSender(_startDate);
    const endDate = localDateSender(_endDate);
    return await this.harvestPlant.harvestPlantsCapacityGet({
      planId,
      endDate,
      startDate,
    });
  }

  //* ************* Temperature **************//
  async getSiteTemperature(siteId: string) {
    return await this.modellingTemperature.temperatureGet({siteId});
  }

  async getCageTemperature(cageId: string) {
    return await this.modellingTemperature.temperatureGet({cageId});
  }

  //* ************* Cages Temperature **************//
  async getCagesTemperature(cageId: string[], _startDate: Date, _endDate: Date) {
    const startDate = localDateSender(_startDate);
    const endDate = localDateSender(_endDate);
    return await this.cageForecast.cageForecastGet({cageId, startDate, endDate, metric: 'temperature'});
  }

  //* ************* Harvest Reports **************//
  async getHarvestReports() {
    return await this.harvestReports.harvestReportsGet();
  }

  async createHarvestReport(report: client.CreateHarvestReport) {
    return await this.harvestReports.harvestReportsPost({createHarvestReport: report});
  }

  async deleteHarvestReport(harvestReportId: string) {
    return await this.harvestReports.harvestReportsHarvestReportIdDelete({harvestReportId});
  }

  async updateHarvestReport(harvestReportId: string, updateHarvestReport: client.UpdateHarvestReport) {
    return await this.harvestReports.harvestReportsHarvestReportIdPatch({
      harvestReportId,
      updateHarvestReport,
    });
  }

  //* ************* Plan Settings **************//

  async getPlanSettings(planId: string) {
    return await this.planSettings.planSettingsPlanIdGet({planId});
  }

  async updatePlanSettings(planId: string, writePlanSettings: client.WritePlanSettings) {
    return await this.planSettings.planSettingsPlanIdPut({planId, writePlanSettings});
  }

  //* ************* Mortality Models **************//

  async getMortalityModel(modelId: string) {
    return await this.mortalityModels.mortalityModelsModelIdGet({modelId});
  }

  async copyMortalityModel(id: string, name: string) {
    return await this.mortalityModels.mortalityModelsCopyPost({copyMortalityModel: {name, id}});
  }

  async getMortalityModels(planId: string) {
    return await this.mortalityModels.mortalityModelsGet({planId});
  }

  async addMortalityModel(addMortalityModel: client.AddMortalityModel) {
    return await this.mortalityModels.mortalityModelsPost({addMortalityModel});
  }

  async updateMortalityModel(modelId: string, editMortalityModel: client.EditMortalityModel) {
    return await this.mortalityModels.mortalityModelsModelIdPut({modelId, editMortalityModel});
  }

  async deleteMortalityModel(modelId: string) {
    return await this.mortalityModels.mortalityModelsModelIdDelete({modelId});
  }

  //* ************* Mortality Model Assignments **************//

  async getMortalityModelAssignment(assignmentId: string) {
    return await this.mortalityModelAssignments.mortalityModelAssignmentsAssignmentIdGet({assignmentId});
  }

  async getMortalityModelAssignments(planId: string) {
    return await this.mortalityModelAssignments.mortalityModelAssignmentsGet({planId});
  }

  async addMortalityModelAssignment(addMortalityModelAssignment: client.AddMortalityModelAssignment) {
    return await this.mortalityModelAssignments.mortalityModelAssignmentsPost({addMortalityModelAssignment});
  }

  async updateMortalityModelAssignment(
    assignmentId: string,
    editMortalityModelAssignment: client.EditMortalityModelAssignment,
  ) {
    return await this.mortalityModelAssignments.mortalityModelAssignmentsAssignmentIdPut({
      assignmentId,
      editMortalityModelAssignment,
    });
  }

  async deleteMortalityModelAssignment(assignmentId: string) {
    return await this.mortalityModelAssignments.mortalityModelAssignmentsAssignmentIdDelete({assignmentId});
  }

  //* ************* Fish Groups **************//

  async getFishGroups(planId: string, fishGroupIds?: string[]) {
    return await this.fishGroups.fishGroupsGet({planId, fishGroupId: fishGroupIds});
  }

  async getFishGroup(planId: string, fishGroupId: string) {
    return await this.fishGroups.fishGroupsFishGroupIdGet({planId, fishGroupId});
  }

  async updateFishGroups(batchPatchFishGroup: client.BatchPatchFishGroup) {
    return await this.fishGroups.fishGroupsPatch({batchPatchFishGroup});
  }

  //* ************* Sensor data **************//
  async getSensorData(connectionId: string) {
    return await this.sensorData.sensorDataStatsGet({connectionId});
  }

  //* ************* Company **************//
  async getCompanies(planId: string) {
    return await this.company.companiesGet({planId});
  }

  async getCompany(companyId: string) {
    return await this.company.companiesCompanyIdGet({companyId});
  }

  async addCompany(addCompany: client.AddCompany) {
    return await this.company.companiesPost({addCompany});
  }

  async editCompany(companyId, editCompany: client.EditCompany) {
    return await this.company.companiesCompanyIdPut({companyId, editCompany});
  }

  async deleteCompany(companyId: string) {
    return await this.company.companiesCompanyIdDelete({companyId});
  }

  //* ************* Fish Group Properties **************//

  async getFishGrpupProperties(planId: string) {
    return await this.fishGroupProperties.fishGroupPropertiesGet({planId});
  }

  async addFishGroupProperty(addFishGroupProperty: client.AddFishGroupProperty) {
    return await this.fishGroupProperties.fishGroupPropertiesPost({
      addFishGroupProperty,
    });
  }

  async editFishGroupProperty(fishPropertyId: string, editFishGroupProperty: client.EditFishGroupProperty) {
    return await this.fishGroupProperties.fishGroupPropertiesFishPropertyIdPut({fishPropertyId, editFishGroupProperty});
  }

  async deleteFishGroupProperty(
    id: client.PlanningFishGroupPropertiesApiFishGroupPropertiesFishPropertyIdDeleteRequest,
  ) {
    return await this.fishGroupProperties.fishGroupPropertiesFishPropertyIdDelete(id);
  }

  // ************* Site Concessions **************//

  async getSiteConcessions(planId: string, siteId?: string) {
    return await this.siteConcession.siteConcessionsGet({planId, siteId});
  }

  async addSiteConcession(siteConcession: client.SiteConcession) {
    return await this.siteConcession.siteConcessionsPost({siteConcession});
  }

  async editSiteConcession(concessionId: string, siteConcession: client.SiteConcession) {
    return await this.siteConcession.siteConcessionsConcessionIdPut({concessionId, siteConcession});
  }

  async deleteSiteConcession(concessionId: string) {
    return await this.siteConcession.siteConcessionsConcessionIdDelete({concessionId});
  }

  // ************* Constructor and APIs **************//

  constructor(basePath: string, axiosInstance: AxiosInstance, accessToken?: string) {
    this.configuration = new client.Configuration({basePath, accessToken});

    this.plan = new client.PlanningPlanApi(this.configuration, undefined, axiosInstance);

    this.site = new client.PlanningSiteApi(this.configuration, undefined, axiosInstance);

    this.siteFallowing = new client.PlanningFallowingApi(this.configuration, undefined, axiosInstance);

    this.cage = new client.PlanningCageApi(this.configuration, undefined, axiosInstance);

    this.input = new client.PlanningInputApi(this.configuration, undefined, axiosInstance);

    this.harvest = new client.PlanningHarvestApi(this.configuration, undefined, axiosInstance);

    this.forecast = new client.PlanningForecastApi(this.configuration, undefined, axiosInstance);

    this.forecastState = new client.PlanningForecastStateApi(this.configuration, undefined, axiosInstance);

    this.optimize = new client.OptimizationOptimizeApi(this.configuration, undefined, axiosInstance);

    this.concession = new client.PlanningConcessionApi(this.configuration, undefined, axiosInstance);

    this.fishType = new client.PlanningFishTypeApi(this.configuration, undefined, axiosInstance);

    this.KPI = new client.KPIsKeyPerformanceIndicatorsApi(this.configuration, undefined, axiosInstance);

    this.optimizationSettings = new client.OptimizationSettingsApi(this.configuration, undefined, axiosInstance);

    this.dataIntegration = new client.DataIntegrationApi(this.configuration, undefined, axiosInstance);

    this.tenant = new client.OrganisationTenantApi(this.configuration, undefined, axiosInstance);

    this.user = new client.OrganisationUserApi(this.configuration, undefined, axiosInstance);

    this.growthModels = new client.ModellingGrowthApi(this.configuration, undefined, axiosInstance);

    this.siteGrowthModelOverrides = new client.ModellingGrowthModelOverridesApi(
      this.configuration,
      undefined,
      axiosInstance,
    );

    this.modellingSGR = new client.ModellingSgrApi(this.configuration, undefined, axiosInstance);

    this.actions = new client.PlanningActionApi(this.configuration, undefined, axiosInstance);

    this.globalSettings = new client.ModellingGlobalSettingsApi(this.configuration, undefined, axiosInstance);

    this.transfer = new client.PlanningTransferApi(this.configuration, undefined, axiosInstance);

    this.navigation = new client.FrontendComponentsNavigationTreeApi(this.configuration, undefined, axiosInstance);

    this.recommendHarvest = new client.PlanningRecommendHarvestApi(this.configuration, undefined, axiosInstance);

    this.productionHistory = new client.ProductionHistoryApi(this.configuration, undefined, axiosInstance);

    this.smoltSupply = new client.PlanningSmoltSupplyApi(this.configuration, undefined, axiosInstance);

    this.priceModels = new client.SalesPriceModelsApi(this.configuration, undefined, axiosInstance);

    this.priceModelAssignments = new client.SalesPriceModelAssignmentApi(this.configuration, undefined, axiosInstance);

    this.qualityGradingModels = new client.QualityGradingModelsApi(this.configuration, undefined, axiosInstance);

    this.qualityGradingModelAssignments = new client.QualityGradingModelAssignmentApi(
      this.configuration,
      undefined,
      axiosInstance,
    );

    this.qualityGradingModelsOverviewTable = new client.QualityGradingModelsOverviewTableApi(
      this.configuration,
      undefined,
      axiosInstance,
    );

    // this.qualityGradingModelTemplates = new client.QualityGradingModelTemplatesApi(
    //   this.configuration,
    //   undefined,
    //   axiosInstance,
    // );

    this.fcrModels = new client.ModellingFeedConversionRatioModelsApi(this.configuration, undefined, axiosInstance);

    this.version = new client.VersionApi(this.configuration, undefined, axiosInstance);

    this.harvestDemand = new client.PlanningHarvestDemandApi(this.configuration, undefined, axiosInstance);

    this.environmentLice = new client.EnvironmentLiceApi(this.configuration, undefined, axiosInstance);

    this.liveFishTransaction = new client.PlanningLiveFishTransactionApi(this.configuration, undefined, axiosInstance);

    this.notifications = new client.NotificationsApi(this.configuration, undefined, axiosInstance);

    this.delousing = new client.PlanningDelousingApi(this.configuration, undefined, axiosInstance);

    this.harvestPlant = new client.PlanningHarvestPlantsApi(this.configuration, undefined, axiosInstance);

    this.modellingTemperature = new client.ModellingTemperatureApi(this.configuration, undefined, axiosInstance);

    this.cageForecast = new client.PlanningCageForecastApi(this.configuration, undefined, axiosInstance);

    this.harvestReports = new client.ReportingHarvestApi(this.configuration, undefined, axiosInstance);

    this.region = new client.PlanningRegionApi(this.configuration, undefined, axiosInstance);

    this.planSettings = new client.PlanningPlanSettingsApi(this.configuration, undefined, axiosInstance);

    this.folder = new client.PlanningFoldersApi(this.configuration, undefined, axiosInstance);

    this.publish = new client.PublishApi(this.configuration, undefined, axiosInstance);

    this.mortalityModels = new client.MortalityModelsApi(this.configuration, undefined, axiosInstance);

    this.fishGroups = new client.PlanningFishGroupApi(this.configuration, undefined, axiosInstance);

    this.mortalityModelAssignments = new client.MortalityModelAssignmentApi(
      this.configuration,
      undefined,
      axiosInstance,
    );

    this.sensorData = new client.SensorDataApi(this.configuration, undefined, axiosInstance);

    this.company = new client.PlanningCompanyApi(this.configuration, undefined, axiosInstance);

    this.fishGroupProperties = new client.PlanningFishGroupPropertiesApi(this.configuration, undefined, axiosInstance);

    this.siteConcession = new client.PlanningSiteConcessionApi(this.configuration, undefined, axiosInstance);
  }
}

const backendCommunicationInstance = new BackendCommunicator(API_URL, axios);

export default backendCommunicationInstance;
